import React from "react";
import {
  Document,
  Page,
  Text as PDFText,
  StyleSheet,
  View,
  Font,
  Image
} from "@react-pdf/renderer";
import { DefaultTheme } from "styled-components";

import { IOrder } from "src/core/orders/api/orders.api";
import { IDrop } from "src/core/drops/api/drops.api";
import {
  Colors,
  formatDate
} from "src/library/utils";
import { formatPrice } from "src/core/prices/utils";
import { InstalledModuleType } from "src/utils/constants";
import { formatDropName } from "src/core/drops/utils/format-drop-name";

import { calculatePriceTotals } from "../../utils/orders";

export interface OrderPDFProps {
  order: IOrder;
  drop: IDrop;
  theme: DefaultTheme;
  installedModules: InstalledModuleType[];
}

const OrderPDF: React.FunctionComponent<OrderPDFProps> = ({
  order, drop, theme, installedModules
}) => {
  const price = React.useMemo(() => (
    calculatePriceTotals(order.orderLines || [], drop)
  ), [ drop, order.orderLines ]);

  // Styled components can not be used with react-pdf
  // This helper generates the styles utilising theme
  const styles = React.useMemo(() => {
    return generateStyles(theme, order.orderStatus?.settings.group);
  }, [ order.orderStatus?.settings.group, theme ]);

  return (
    <Document>
      <Page
        size="A4"
        style={styles.page}
      >
        <View style={styles.container}>
          <View
            style={{
              ...styles.section,
              ...styles.headerContainer
            }}
          >
            <PDFText style={styles.title}>
              Order Details
            </PDFText>

            <Image
              style={styles.logo}
              src="/assets/web/pdf-logo.png"
            />
          </View>

          <View style={styles.section}>
            <View style={styles.orderInformation}>
              {/* customer details */}

              <div>
                <PDFText style={styles.bold}>
                  {drop.customer?.name || ""}
                </PDFText>

                <PDFText style={{ marginBottom: theme.spacing.margins.sm }}>
                  {formatDropName(drop)}
                </PDFText>

                <PDFText style={styles.addressLine}>
                  {drop.address.addressLine1}
                </PDFText>

                {drop.address.addressLine2 && (
                  <PDFText style={styles.addressLine}>
                    {drop.address.addressLine2}
                  </PDFText>
                )}

                {drop.address.addressLine3 && (
                  <PDFText style={styles.addressLine}>
                    {drop.address.addressLine3}
                  </PDFText>
                )}

                {drop.address.city && (
                  <PDFText style={styles.addressLine}>
                    {drop.address.city}
                  </PDFText>
                )}

                <PDFText style={styles.addressLine}>
                  {drop.address.postcode}
                </PDFText>
              </div>

              <div style={styles.orderDetails}>
                <div style={styles.field}>
                  <div style={styles.fieldHeader}>
                    <PDFText>
                      PO Number:
                    </PDFText>
                  </div>

                  <PDFText>
                    {` ${order.customerReference || "-"}`}
                  </PDFText>
                </div>

                {installedModules?.includes("rjl") && (
                  <div style={styles.field}>
                    <div style={styles.fieldHeader}>
                      <PDFText>
                        Delivery Number:
                      </PDFText>
                    </div>

                    <PDFText>
                      {`  #${order.rjlDeliveryNumber}`}
                    </PDFText>
                  </div>
                )}

                <div style={styles.field}>
                  <div style={styles.fieldHeader}>
                    <PDFText>
                      Status:
                    </PDFText>
                  </div>

                  <PDFText
                    style={{
                      ...styles.orderStatus,
                      ...styles.bold
                    }}
                  >
                    {` ${order.orderStatus?.name || "-"}`}
                  </PDFText>
                </div>

                <div style={styles.field}>
                  <div style={styles.fieldHeader}>
                    <PDFText>
                      Delivery Date:
                    </PDFText>
                  </div>

                  <PDFText>
                    {` ${formatDate(order.orderDeliveryDate, "ddd Do MMMM YYYY", true)}`}
                  </PDFText>
                </div>
              </div>
            </View>
          </View>

          {order.orderLines.length === 0 ?
            (
              <View style={styles.noOrderLines}>
                <PDFText style={styles.fieldHeader}>
                  No products found in this order.
                </PDFText>
              </View>
            ) : (
              <>
                <View>
                  <div style={styles.tableHeaders}>
                    <div style={styles.coloumn}>
                      <PDFText style={styles.bold}>
                        Product Code
                      </PDFText>
                    </div>

                    <div style={styles.coloumnDoubleWidth}>
                      <PDFText style={styles.bold}>
                        Product Name
                      </PDFText>
                    </div>

                    <div style={styles.coloumn}>
                      <PDFText style={styles.bold}>
                        Quantity
                      </PDFText>
                    </div>

                    <div style={styles.coloumn}>
                      <PDFText style={styles.bold}>
                        Unit Price *
                      </PDFText>
                    </div>

                    <div style={styles.coloumn}>
                      <PDFText style={styles.bold}>
                        Total
                      </PDFText>
                    </div>
                  </div>

                  {/* product order lines */}

                  <div>
                    {order.orderLines.map(ol => (
                      <div
                        style={styles.tableRow}
                        key={ol.id}
                      >
                        <div style={styles.coloumn}>
                          <PDFText>
                            {ol.product.partNumber}
                          </PDFText>
                        </div>

                        <div style={styles.coloumnDoubleWidth}>
                          <PDFText>
                            {ol.product.description}
                          </PDFText>
                        </div>

                        <div style={styles.coloumn}>
                          <PDFText>
                            {ol.quantity}
                          </PDFText>
                        </div>

                        <div style={styles.coloumn}>
                          <PDFText>
                            {formatPrice(ol.price, 2, drop.priceCode?.currency)}
                          </PDFText>
                        </div>

                        <div style={styles.coloumn}>
                          <PDFText>
                            {ol.price && formatPrice(ol.price * ol.quantity, 2, drop.priceCode?.currency)}
                          </PDFText>
                        </div>
                      </div>
                    ))}
                  </div>
                </View>

                <View style={styles.totalPrice}>
                  <PDFText style={styles.bold}>
                    Total:
                  </PDFText>

                  <PDFText
                    style={{
                      marginLeft: 10,
                      marginRight: 20
                    }}
                  >
                    {` ${formatPrice(price.total, 2, drop.priceCode?.currency)}`}
                  </PDFText>
                </View>

                <View style={styles.additionalInformation}>
                  <PDFText>
                    * Prices exclude VAT
                  </PDFText>

                  <PDFText style={{ marginTop: 5 }}>
                    This document is a printable format of the order as displayed on the web portal, and is not an invoice.
                  </PDFText>
                </View>
              </>
            )}
        </View>
      </Page>
    </Document>
  );
};

// Helper to generate the styles to use within PDF.
// It is not possible to use styled-components with react-pdf due to the nature of
// styled components rendering in the <header> which will not exist within the rendered PDF.
const generateStyles = (theme: DefaultTheme, orderStatusGroup?: string) => {
  Font.register({
    family: "Roboto",
    fonts: [
      { src: "/assets/fonts/Roboto/Roboto-Regular.ttf" },
      {
        src: "/assets/fonts/Roboto/Roboto-Italic.ttf",
        fontStyle: "italic"
      },
      {
        src: "/assets/fonts/Roboto/Roboto-Bold.ttf",
        fontWeight: 700
      }
    ]
  });

  let orderStatusGroupColorCode = "inherit";

  // if order status provided, set the color based on theme
  if (orderStatusGroup) {
    const orderStatusGroupColorKey = `${orderStatusGroup.toLowerCase()}Order` as keyof Colors;

    orderStatusGroupColorCode = theme.colors[ orderStatusGroupColorKey ];
  }

  return StyleSheet.create({
    page: { padding: `${theme.spacing.padding.lg} 0` },
    container: {
      width: "95%",
      background: "#FFF",
      margin: "0 auto",
      fontFamily: "Roboto",
      fontSize: 12,
      fontWeight: theme.fonts.weights.normal,
      color: theme.fonts.colors.primary,
      lineHeight: 1.1
    },
    title: { fontSize: theme.fonts.sizes.h2 },
    section: {
      marginBottom: theme.spacing.margins.lg,
      borderBottom: `4px solid ${theme.colors.primaryLight}`
    },
    headerContainer: {
      height: 70,
      display: "flex",
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center"
    },
    logo: { width: 50 },
    bold: { fontWeight: 700 },
    orderInformation: {
      display: "flex",
      flexDirection: "row",
      justifyContent: "space-between",
      paddingLeft: theme.spacing.padding.lg,
      paddingRight: theme.spacing.padding.lg,
      paddingBottom: theme.spacing.padding.xl
    },
    addressLine: { fontStyle: "italic" },
    orderDetails: {
      height: 75,
      width: 270,
      padding: theme.spacing.padding.sm,
      backgroundColor: theme.colors.ziggurat,
      borderRadius: theme.borderRadius
    },
    field: {
      display: "flex",
      flexDirection: "row",
      alignItems: "center"
    },
    fieldHeader: {
      width: 100,
      fontWeight: 700
    },
    noOrderLines: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center"
    },
    tableHeaders: {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      borderBottom: `1px solid ${theme.colors.lightGrey}`,
      fontWeight: "bold"
    },
    tableRow: {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      borderBottom: `1px solid ${theme.colors.lightGrey}`
    },
    coloumn: {
      display: "flex",
      flex: 1,
      justifyContent: "center",
      alignItems: "center",
      padding: theme.spacing.padding.sm
    },
    coloumnDoubleWidth: {
      display: "flex",
      flexDirection: "row",
      flex: 2,
      alignItems: "center",
      justifyContent: "flex-start",
      padding: theme.spacing.padding.sm
    },
    totalPrice: {
      margin: `${theme.spacing.margins.lg} 0`,
      display: "flex",
      flexDirection: "row",
      justifyContent: "flex-end"
    },
    additionalInformation: {
      position: "absolute",
      bottom: 0,
      left: 0,
      fontSize: 9,
      fontStyle: "italic"
    },
    orderStatus: { color: orderStatusGroupColorCode }
  });
};

export default OrderPDF;
